"use client";

import { SITE } from "@/lib/site";
import { LEAD_KEY, newEventId, metaServer } from "@/components/Tracking";
import { klaviyoSubscribeWithin } from "@/lib/klaviyo";

// Web3Forms-backed form. Plain HTML POST (static-export friendly, works
// without JS); Web3Forms redirects to `redirect` on success. The hidden
// botcheck checkbox is Web3Forms' honeypot spam filter.
//
// `lead` (e.g. { formId: "4", formName: "Venue hire enquiry" }) marks the form
// as a tracked conversion: on submit the event details are kept in
// sessionStorage and the confirmation page fires GA4 generate_lead + Meta Lead
// with them (components/Tracking.js).
export default function W3Form({
  subject,
  fields,
  button = "Send",
  redirect = "/thank-you/",
  lead,
  optIn = true,
}) {
  const onSubmit = (e) => {
    const form = e.currentTarget;
    const v = (name) => {
      const el = form.elements.namedItem(name);
      return el && "value" in el ? el.value : "";
    };
    // Klaviyo: only when the visitor ticked the opt-in box. Hold the submit until Klaviyo confirms
    // (max 4s), then send the form on to Web3Forms, so the sign-up is not lost when the page navigates.
    const optedIn = form.elements.namedItem("klaviyo_opt_in")?.checked && !form.dataset.klaviyoDone;
    if (optedIn) {
      e.preventDefault();
      if (form.dataset.sending) return;
      form.dataset.sending = "1";
      const name = [v("name"), v("last_name")].filter(Boolean).join(" ");
      klaviyoSubscribeWithin(4000, { email: v("email"), name, source: `${subject} (website form)` }).then((ok) => {
        form.dataset.klaviyoDone = "1";
        const flag = form.elements.namedItem("klaviyo_subscribed");
        if (flag) flag.value = ok ? "yes" : "failed";
        trackLead();
        HTMLFormElement.prototype.submit.call(form);
      });
      return;
    }
    trackLead();

    function trackLead() {
      if (!lead) return;
      // Meta CAPI: send the server Lead now, with contact details (hashed server-side), when the pixel
      // is running (cookie consent given). The confirmation page fires the browser Lead with the same ID.
      const eventId = newEventId();
      const serverSent = !!window.fbq;
      if (serverSent) metaServer("Lead", eventId, { content_name: lead.formName, content_category: v("event_type") || "Event enquiry" }, { email: v("email"), phone: v("phone"), name: v("name") });
      try {
        sessionStorage.setItem(LEAD_KEY, JSON.stringify({
          meta_event_id: eventId,
          meta_server_sent: serverSent,
          form_id: lead.formId,
          form_name: lead.formName,
          event_type: v("event_type"),
          guests: Number(v("guests")) || undefined,
          event_date: v("event_date"),
          start_time: v("start_time"),
          catering_style: v("catering_style"),
          entertainment: v("entertainment"),
          budget: v("budget"),
          page_path: window.location.pathname,
        }));
      } catch {}
    }
  };

  return (
    <form action="https://api.web3forms.com/submit" method="POST" className="wform" onSubmit={onSubmit}>
      <input type="hidden" name="access_key" value={SITE.web3formsKey} />
      <input type="hidden" name="subject" value={subject} />
      <input type="hidden" name="from_name" value="luluwild.co.uk website" />
      <input type="hidden" name="redirect" value={SITE.formBase + redirect} />
      <input
        type="checkbox"
        name="botcheck"
        className="wform-botcheck"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      {fields.map((f) => f.type === "checkbox" ? (
        <label key={f.name} className="wform-optin wform-field--wide">
          <input type="checkbox" name={f.name} value={f.value || "yes"} required={f.required} />
          <span>
            {f.label}
            {f.required && <em aria-hidden="true"> *</em>}
          </span>
        </label>
      ) : (
        <label key={f.name} className={`wform-field ${f.wide ? "wform-field--wide" : ""}`}>
          <span>
            {f.label}
            {f.required && <em aria-hidden="true"> *</em>}
          </span>
          {f.type === "textarea" ? (
            <textarea
              name={f.name}
              required={f.required}
              rows={5}
              placeholder={f.placeholder}
            />
          ) : f.type === "select" ? (
            <select name={f.name} required={f.required} defaultValue="">
              <option value="" disabled>
                {f.placeholder || "Choose…"}
              </option>
              {f.options.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          ) : (
            <input
              type={f.type || "text"}
              name={f.name}
              required={f.required}
              placeholder={f.placeholder}
              min={f.min}
            />
          )}
        </label>
      ))}
      {optIn && <input type="hidden" name="klaviyo_subscribed" value="not ticked" />}
      {optIn && (
        <label className="wform-optin wform-field--wide">
          <input type="checkbox" name="klaviyo_opt_in" value="yes" />
          <span>Keep me posted: send me Lulu&rsquo;s news, new menus and offers by email. You can unsubscribe at any time.</span>
        </label>
      )}
      <div className="wform-field--wide center" style={{ marginTop: "0.6rem" }}>
        <button type="submit" className="btn btn--solid">{button}</button>
      </div>
    </form>
  );
}
